import { useDispatch, useSelector } from "react-redux";
import {
  addToFavorites,
  removeFromFavorites,
} from "store/slices/favoritesSlice";

import { ProductImage } from "common";
import { getCurrentPrice } from "config/config";
import { getPrimaryProductImage } from "utils/productImages";

import { badgeFor, stockState, unitLabel } from "./productPresentation";

import { ProductCard } from "@/design-system";

/**
 * Grid listing card — a thin adapter mapping a NetSuite product onto the design
 * system's `ProductCard` (`Dens N Dente Tailwind.dc.html:655`, `pc-compact` density).
 * Same adapter pattern as `common/navigation/Pagination.jsx`: the DS owns the markup,
 * this file owns the domain mapping.
 *
 * Quantity still belongs to `ListProduct` (`useQuantityHandlers`), which is why the
 * card is driven in controlled mode. So does the quick look — this only forwards
 * `onQuickLook`, and omitting it hides the button, because the DS gates it on the prop.
 *
 * The old card's `manufacturer | MPN: xxx` line is split across the reference's two
 * slots rather than dropped: `manufacturer` becomes the uppercase brand line above the
 * title, and `mpn` takes the mono row below it, which is exactly what that row is for.
 * `itemid` stays the title.
 *
 * Two things the old card showed are deliberately gone, not lost:
 * • `<DeliveryEstimate>` pill — superseded by the DS stock row.
 * • cart-quantity bubble — the DS's `withQuantity` layout has no icon to anchor it to.
 *
 * Quick look came back as the design system's own `dnd-pc__ql`: a glass pill that slides
 * up from the bottom of the media on hover, instead of the old button parked in the
 * middle of the image where it fought the navigate target.
 *
 * Not shown because there's no data: the rating row
 * (`custitem_customer_review_rating` is fetched but has never been displayed, and no
 * review-count field exists).
 *
 * Stock state, badge and unit all come from `./productPresentation`, shared with the
 * list row and the quick look so the same item can't read differently in each.
 */
export default function ProductInListGrid({
  product,
  quantity,
  actualQuantity,
  handleQuantityChange,
  handleAddToCart,
  handleNavigate,
  onQuickLook,
}) {
  const dispatch = useDispatch();
  const { id, itemid, mpn } = product;

  const userInfo = useSelector((state) => state.user.info);
  const favorites = useSelector((state) => state.favorites.items);
  const isFavorite = favorites.includes(Number(id));

  const { basePrice, currentPrice, savePrice } = getCurrentPrice(
    product,
    quantity,
    userInfo?.category
  );

  const toggleFavorite = () => {
    dispatch(
      isFavorite ? removeFromFavorites(Number(id)) : addToFavorites(Number(id))
    );
  };

  return (
    <ProductCard
      // `flex-1`: in a carousel the item wrapper is `flex`, so the card stretches to the
      // row's full height (upstream passes class-name="flex-1" the same way). In the
      // listing's CSS grid the class is inert — grid items ignore flex-grow.
      className="pc-compact flex-1"
      // The DS source uses a raw <img>; CLAUDE.md rule 2 requires ProductImage for the
      // onError fallback and NetSuite base-URL handling, so it comes in as a node.
      media={
        <ProductImage
          src={getPrimaryProductImage(product)}
          alt={itemid || "Product image"}
          className="w-full h-full object-cover [transition:transform_var(--duration-slow)_var(--ease-out)] group-hover:scale-105"
        />
      }
      brand={product.manufacturer}
      name={itemid}
      // The mono row is the manufacturer part number. `undefined` rather than "" so
      // the card drops the row entirely when a product has no MPN.
      sku={mpn || undefined}
      price={currentPrice}
      originalPrice={savePrice > 0 ? basePrice : undefined}
      unit={unitLabel(product)}
      stock={stockState(product)}
      badge={badgeFor(product)}
      withQuantity
      qty={quantity}
      onQty={(value) => handleQuantityChange(id, value)}
      // Buy-X-get-Y means the cart receives more than was selected; the label says so.
      addQty={actualQuantity}
      // The reference's listing card passes the same short label
      // (`Dens N Dente Tailwind.dc.html:741`) — "Add to cart" doesn't fit beside the
      // stepper on a 220px track.
      addLabel="Add"
      favorite={isFavorite}
      onFavorite={toggleFavorite}
      onQuickLook={onQuickLook}
      onAdd={handleAddToCart}
      onClick={handleNavigate}
    />
  );
}
